import React from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import FolderIcon from "@mui/icons-material/Folder";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  candidateSchema,
  fetchCandidate,
  ICandidate,
} from "../../data/Candidates";
import CandidateContactForm from "./CandidateContactForm";
import CandidateBioForm from "./CandidateBioForm";
import NotesForm from "../misc/NotesForm";
import CandidateOfficeForm from "./CandidateOfficeForm";
import LinearProgress from "@mui/material/LinearProgress";
import { useSnackbar } from "notistack";

const defaultValues: ICandidate = {
  name: "",
  title: null,
  status: "",
  id_number: "",
  phone_number_primary: "",
  phone_number_secondary: "",
  email: "",
  email_secondary: "",
  address: "",
  location: null,
  notice_period: 0,
  gender: null,
  qualifies_for_bee: false,
  valid_work_permit: false,
  permanent_resident: false,
  has_own_car: false,
  salary: "",
  nationality: null,
  driving_licence: null,
  areas: [],
  languages: [],
  qualifications: [],
  notes: [],
  experience: [],
  technical_skills: [],
  levels: [],
  specialties: [],
  created_by: "",
  modified: new Date(),
};

export default function CandidateUpdate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: ICandidate) =>
      axios.patch<ICandidate>(`/candidates/${id}/`, data).then((r) => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: () => axios.delete(`/candidates/${id}/`),
  });

  const submitting = updateMutation.isPending || deleteMutation.isPending;

  const methods = useForm<ICandidate>({
    disabled: submitting,
    defaultValues,
    resolver: yupResolver(candidateSchema),
  });
  const { handleSubmit, reset } = methods;

  const {
    data: candidate,
    isLoading: candidateLoading,
    error: candidateError,
  } = useQuery({
    queryKey: ["candidate", id],
    queryFn: () => fetchCandidate(id!),
    enabled: !!id,
  });

  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (candidateError) {
      enqueueSnackbar("Failed to load candidate.", { variant: "error" });
    }
  }, [candidateError, enqueueSnackbar]);

  React.useEffect(() => {
    if (!candidateLoading && candidate) {
      reset(candidate);
      document.title = candidate.name || "Candidates";
    }
  }, [id, candidateLoading, candidate, reset]);

  const handleUpdate: SubmitHandler<ICandidate> = (data) => {
    updateMutation.mutate(data, {
      onSuccess: (responseData) => {
        queryClient.setQueryData(["candidate", id], responseData);
        reset(responseData);
        enqueueSnackbar(`${responseData.name} updated.`, { variant: "success" });
      },
      onError: () => enqueueSnackbar("Failed to update candidate.", { variant: "error" }),
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        enqueueSnackbar("Candidate deleted.", { variant: "warning" });
        setDialogOpen(false);
        navigate("/candidates");
      },
      onError: () => {
        setDialogOpen(false);
        enqueueSnackbar("Failed to delete candidate.", { variant: "error" });
      },
    });
  };

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(handleUpdate)}
        autoComplete="off"
        sx={{
          display: "grid",
          columnGap: 3,
          ml: 3,
          mr: 3,
          gridTemplateRows: `auto 1fr`,
          gridTemplateColumns: `1fr 1fr 1fr 1fr`,
          gridTemplateAreas: `
                  "toolbar toolbar toolbar toolbar"
                  "candidate bio office notes"
                  `,
        }}
      >
        <Box
          sx={{
            gridArea: "toolbar",
            borderBottom: 1,
            borderTop: 1,
            borderColor: "grey.300",
          }}
        >
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              CANDIDATE
            </Typography>
            <Stack spacing={2} direction="row">
              {!candidateLoading && candidate?.folder ? (
                <Button
                  href={candidate.folder}
                  target="_blank"
                  rel="noreferrer noopener"
                  startIcon={<FolderIcon />}
                >
                  Folder
                </Button>
              ) : null}
              <Button
                onClick={handleDialogOpen}
                startIcon={<DeleteIcon />}
                loading={submitting}
                loadingPosition="start"
                variant="text"
              >
                Delete
              </Button>
              <Button
                type="submit"
                startIcon={<SaveIcon />}
                loading={submitting}
                loadingPosition="start"
                variant="contained"
              >
                Update
              </Button>
            </Stack>
          </Toolbar>
          {candidateLoading ? <LinearProgress sx={{ mt: -0.5 }} /> : null}
        </Box>
        <Box sx={{ gridArea: "candidate" }}>
          <CandidateContactForm candidateLoading={candidateLoading} />
        </Box>
        <Box sx={{ gridArea: "bio" }}>
          <CandidateBioForm candidateLoading={candidateLoading} />
        </Box>
        <Box sx={{ gridArea: "office" }}>
          <CandidateOfficeForm candidateLoading={candidateLoading} />
        </Box>
        <Box sx={{ gridArea: "notes" }}>
          <NotesForm disabled={submitting} parentLoading={candidateLoading} />
        </Box>
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Delete this candidate?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDialogClose}
              loading={submitting}
              variant="text"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
              loading={submitting}
              loadingPosition="start"
              variant="text"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </FormProvider>
  );
}
